import * as d3 from "d3";

export const parseSystemDate = (input) => new Date(input);

export const fixedPrecision = (value, decimals = 4) =>
  parseFloat(value.toFixed(decimals));

export const parseFloatOrNull = (value) =>
  value === "n/a"
    ? null
    : value.endsWith("%") === true
    ? fixedPrecision(parseFloat(value.trim("%")) / 100, 6)
    : parseFloat(value);

const dateTimeParser = d3.timeParse("%Y-%m-%dT%H:%M:%S");

const parseKeys = (head, translations = {}) =>
  head.split(/\s+/).map((key) => translations[key] || key);

const timeAndNumbers = (row, keys, skipped = []) =>
  row
    .split(/\s+/)
    .map((value, index) =>
      skipped.length > 0 && skipped.includes(keys[index])
        ? null
        : [
            keys[index],
            index > 0 ? parseFloatOrNull(value) : dateTimeParser(value),
          ]
    )
    .filter((item) => item !== null);
const appendToList = (list = [], key, value) => [
  ...list,
  { name: key, speed: value },
];

const SECONDS_PER_MINUTE = 60;
const GB_TO_MBITS = 8 * 1000;

const toSpeedsList = ({ skipped, interval = 10, listName = "subscribers" }) => {
  let volume = 0;

  const mind = (key, value) => {
    if (key === "volume") {
      volume = value * GB_TO_MBITS;
    }
    return value;
  };

  const calcSpeed = (value) /*GB*/ =>
    fixedPrecision((volume * value) / (interval * SECONDS_PER_MINUTE), 4);

  return (reading, [key, value]) =>
    skipped.includes(key) === true
      ? reading
      : key === "time" || key === "volume"
      ? { ...reading, [key]: mind(key, value) }
      : {
          ...reading,
          [listName]: appendToList(reading[listName], key, calcSpeed(value)),
        };
};

export const parseSubscribersSpeed = (input, interval = 10) => {
  const lines = input.length === 0 ? [] : input.trim().split("\n");
  if (lines.length < 2) {
    return { timeline: [], totals: {} };
  }
  const [head, ...rows] = lines;
  const translations = {
    REST: "REST",
    TIME: "time",
    GIGABYTES: "volume",
  };
  const keys = parseKeys(head, translations);
  const skipped = [];
  const calcTheseValuesSpeeds = toSpeedsList({ skipped, interval });
  const items = rows.map((row) =>
    timeAndNumbers(row, keys, skipped, translations).reduce(
      calcTheseValuesSpeeds,
      {}
    )
  );
  const { time, ...totals } = items.pop();
  return { timeline: items, totals };
};

export const parseSubscriberBandwidthShare = (
  input,
  options = { omitTimeline: false, omitTotals: false, precision: 4 }
) => {
  const lines = input.length === 0 ? [] : input.trim().split("\n");
  if (lines.length < 2) {
    return { timeline: [], totals: {} };
  }
  const last = lines.pop();
  const [head, ...rows] = lines;
  const translations = {
    GIGABYTES: "volume",
    REST: "REST",
    TIME: "time",
    TOTAL: "total",
  };
  const keys = parseKeys(head, translations);
  const skipped = [];
  const appendtoSubscribers = (
    { subscribers = [], volume = 0 },
    key,
    value
  ) => [
    ...subscribers,
    {
      name: key,
      share: value,
      volume: fixedPrecision(volume * value, options.precision),
    },
  ];
  const toSubscribersList = (reading, [key, value]) =>
    skipped.includes(key) === true
      ? reading
      : key === "time" || key === "volume"
      ? { ...reading, [key]: value }
      : {
          ...reading,
          subscribers: appendtoSubscribers(reading, key, value),
        };

  const parseTimeline = () =>
    rows.map((row) =>
      timeAndNumbers(row, keys, skipped).reduce(toSubscribersList, {})
    );
  const totalsSkipped = ["time"];
  const parseTotals = () =>
    timeAndNumbers(last, keys, totalsSkipped).reduce(toSubscribersList, {});
  return {
    ...(options.omitTimeline ? {} : { timeline: parseTimeline() }),
    ...(options.omitTotals ? {} : { totals: parseTotals() }),
  };
};
